import { useRef, useMemo, useState } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { ChevronRight, AtSign } from "lucide-react"
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field"
import { PlateEditor, parseValue, DATA_TYPE_ITEMS } from "@/components/ui/plate-editor"
import { Button } from "@/components/ui/button"
import { Kbd, KbdGroup } from "@/components/ui/kbd"
import { Collapsible, CollapsibleTrigger, AnimatedCollapsibleContent } from "@/components/ui/collapsible"
import { FieldTooltip } from "@/components/ui/field-tooltip"

export function AgentForm({ agent, onSubmit, submitLabel }) {
  const triggerRef = useRef(null)
  const dataRef = useRef(null)
  const requestRef = useRef(null)
  const restrictionsRef = useRef(null)
  const submitButtonRef = useRef(null)
  const [restrictionsOpen, setRestrictionsOpen] = useState(() => Boolean(agent?.restrictions))

  // Get errors from Inertia shared props (set by controller redirects)
  const { errors: pageErrors } = usePage().props

  const { data, setData, processing, errors: formErrors } = useForm({
    trigger: agent?.trigger || "",
    data: agent?.data || "",
    request: agent?.request || "",
    restrictions: agent?.restrictions || "",
  })

  // Merge errors from both sources (page errors take precedence)
  const errors = { ...formErrors, ...pageErrors }

  // Build a lookup from data type key to icon component
  const dataTypeIconMap = useMemo(() => {
    return DATA_TYPE_ITEMS.reduce((acc, item) => {
      acc[item.key] = item.icon
      return acc
    }, {})
  }, [])

  // Build a lookup from extracted email data: field name → extracted value
  const extractedDataMap = useMemo(() => {
    const fields = agent?.preview_extracted_data?.fields
    if (!fields) return {}
    return fields.reduce((acc, f) => {
      if (f.name && f.value) acc[f.name] = f.value
      return acc
    }, {})
  }, [agent?.preview_extracted_data])

  // Derive dynamic mention items for the request editor from data field names
  const requestMentionItems = useMemo(() => {
    if (!data.data) return []

    const nodes = parseValue(data.data)
    const items = []

    function extractFromListItems(nodeList) {
      for (const node of nodeList) {
        if (node.type === "li" && node.children) {
          // Look inside lic (list item content) for the field name and data type
          for (const child of node.children) {
            if (child.type === "lic" && child.children) {
              const textParts = []
              let dataType = null
              for (const inner of child.children) {
                if (inner.type === "mention") {
                  dataType = inner.key
                  break
                }
                if (inner.text != null) {
                  textParts.push(inner.text)
                }
              }
              const fieldName = textParts.join("").trim()
              if (fieldName) {
                const key = fieldName.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")
                const icon = (dataType && dataTypeIconMap[dataType]) || AtSign
                const item = { key, text: fieldName, icon }
                if (dataType) item.dataType = dataType
                const extractedValue = extractedDataMap[fieldName]
                if (extractedValue) item.description = extractedValue
                items.push(item)
              }
            }
          }
        }
        // Recurse into lists (ul/ol)
        if (node.children && (node.type === "ul" || node.type === "ol")) {
          extractFromListItems(node.children)
        }
      }
    }

    extractFromListItems(nodes)
    return items
  }, [data.data, dataTypeIconMap, extractedDataMap])

  function handleSubmit(e) {
    e.preventDefault()
    onSubmit(data)
  }

  return (
    <form id="agent-edit-form" onSubmit={handleSubmit}>
      <Field data-size="trigger" className="gap-4 px-6 py-4">
        <FieldLabel
          htmlFor="trigger"
          className="flex flex-col items-start gap-0"
          onClick={() => triggerRef.current?.focusAtStart()}
        >
          <span className="flex cursor-pointer items-center gap-1">
            <span className="text-xl font-medium">When should the agent run?</span>
            <FieldTooltip>
              <div className="text-sm font-medium mb-1">When to run</div>
              <ul className="text-[13px] list-disc pl-5 space-y-1">
                <li>Use email-search style rules</li>
                <li>
                  <span className="pr-1">Filters:</span>
                  <KbdGroup>
                    <Kbd>from:</Kbd>
                    <Kbd>to:</Kbd>
                    <Kbd>cc:</Kbd>
                    <Kbd>bcc:</Kbd>
                    <Kbd>subject:</Kbd>
                    <Kbd>body:</Kbd>
                    <Kbd>attachment:</Kbd>
                  </KbdGroup>
                </li>
                <li>
                  <span className="pr-1">Operators:</span>
                  <KbdGroup>
                    <Kbd>AND</Kbd>
                    <Kbd>OR</Kbd>
                  </KbdGroup>
                </li>
                <li>
                  <span className="pr-1">Exact matches:</span>
                  <Kbd>"quoted phrase"</Kbd>
                </li>
                <li>
                  <span className="pr-1">Grouping:</span>
                  <Kbd>subject:"payment received" OR body:"order #"</Kbd>
                </li>
              </ul>
            </FieldTooltip>
          </span>
          <FieldDescription className="cursor-pointer text-sm">
            Describe what must be true about an incoming email for the agent to start
          </FieldDescription>
        </FieldLabel>
        <PlateEditor
          ref={triggerRef}
          id="trigger"
          value={data.trigger}
          onChange={(value) => setData("trigger", value)}
          onTab={() => dataRef.current?.focusAtStart()}
          onArrowDown={() => dataRef.current?.focusAtStart()}
          placeholder="Describe the trigger..."
          disableMentions
        />
        {errors.trigger && (
          <FieldError>
            {Array.isArray(errors.trigger) ? errors.trigger.join(", ") : errors.trigger}
          </FieldError>
        )}
      </Field>

      <Field data-size="data" className="gap-4 px-6 py-4">
        <FieldLabel
          htmlFor="data"
          className="flex flex-col items-start gap-0"
          onClick={() => dataRef.current?.focusAtStart()}
        >
          <span className="flex cursor-pointer items-center gap-1">
            <span className="text-xl font-medium">What data should the agent extract?</span>
            <FieldTooltip>
              <div className="text-sm font-medium mb-1">What to extract</div>
              <ul className="text-[13px] list-disc pl-5 space-y-1">
                <li>Type <Kbd>@</Kbd> to define a field type</li>
                <li>Choose the most specific type possible</li>
                <li>Provide examples for additional clarity</li>
              </ul>
            </FieldTooltip>
          </span>
          <FieldDescription className="cursor-pointer text-sm">
            List the exact pieces of information to pull from the email
          </FieldDescription>
        </FieldLabel>
        <PlateEditor
          ref={dataRef}
          id="data"
          value={data.data}
          onChange={(value) => setData("data", value)}
          onTab={() => requestRef.current?.focusAtStart()}
          onArrowUp={() => triggerRef.current?.focusAtEnd()}
          onArrowDown={() => requestRef.current?.focusAtStart()}
          placeholder="Describe the data..."
          mentionItems={DATA_TYPE_ITEMS}
          mentionColor="sky"
        />
        {errors.data && (
          <FieldError>
            {Array.isArray(errors.data) ? errors.data.join(", ") : errors.data}
          </FieldError>
        )}
      </Field>
      
      <Field data-size="request" className="gap-4 px-6 py-4">
        <FieldLabel
          htmlFor="request"
          className="flex flex-col items-start gap-0"
          onClick={() => requestRef.current?.focusAtStart()}
        >
          <span className="flex cursor-pointer items-center gap-1">
            <span className="text-xl font-medium">Where should the data go?</span>
            <FieldTooltip>
              <div className="text-sm font-medium mb-1">Where to send</div>
              <ul className="text-[13px] list-disc pl-5 space-y-1">
                <li>Specify the request type and endpoint</li>
                <li>Include any headers or authentication</li>
                <li>Type <Kbd>/</Kbd> and select <Kbd>Code</Kbd> to define a payload</li>
                <li>Insert extracted fields with <Kbd>@</Kbd></li>
              </ul>
            </FieldTooltip>
          </span>
          <FieldDescription className="cursor-pointer text-sm ">
            Describe the destination and how the extracted fields should be sent
          </FieldDescription>
        </FieldLabel>
        <PlateEditor
          ref={requestRef}
          id="request"
          value={data.request}
          onChange={(value) => setData("request", value)}
          onTab={() => {
            if (restrictionsOpen) {
              restrictionsRef.current?.focusAtStart()
            } else {
              submitButtonRef.current?.focus()
            }
          }}
          onArrowUp={() => dataRef.current?.focusAtEnd()}
          onArrowDown={() => {
            if (restrictionsOpen) {
              restrictionsRef.current?.focusAtStart()
            } else {
              submitButtonRef.current?.focus()
            }
          }}
          placeholder="Describe the request..."
          mentionItems={requestMentionItems}
          extractedValues={extractedDataMap}
        />
        {errors.request && (
          <FieldError>
            {Array.isArray(errors.request) ? errors.request.join(", ") : errors.request}
          </FieldError>
        )}
      </Field>

      {/*
      <Collapsible
        open={restrictionsOpen}
        onOpenChange={(open) => {
          setRestrictionsOpen(open)
          if (open) {
            setTimeout(() => restrictionsRef.current?.focusAtStart(), 0)
          }
        }}
       className="gap-4 px-8 py-4"
      >
        <Field>
          <CollapsibleTrigger asChild>
            <div className="group flex justify-between">
              <FieldLabel
                htmlFor="restrictions"
                className="flex flex-col items-start gap-0"
              >
                <span className="flex items-baseline gap-2">
                  <span className="flex cursor-pointer items-center gap-1 text-xl font-medium">
                    What should the agent avoid?
                    <FieldTooltip>
                      <div className="text-sm font-medium mb-1">Restrictions</div>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Use restrictions as guardrails to prevent accidental sends, sensitive data leaks, and noisy triggers</li>
                        <li>Add rules for emails to ignore, fields to exclude, and destinations to block</li>
                      </ul>
                    </FieldTooltip>
                  </span>
                  <span className="text-muted-foreground text-sm font-normal">Optional</span>
                </span>
                <AnimatedCollapsibleContent open={restrictionsOpen}>
                  <FieldDescription className="text-[13px]">
                    Set boundaries and limitations for the agent's behavior
                  </FieldDescription>
                </AnimatedCollapsibleContent>
              </FieldLabel>
              <Button type="button" variant="ghost" size="sm" className="group/chevron h-7 w-7 p-0" tabIndex={-1}>
                <ChevronRight className="h-4 w-4 text-muted-foreground transition-all duration-200 ease-out group-hover/chevron:text-foreground group-data-[state=open]:rotate-90" />
              </Button>
            </div>
          </CollapsibleTrigger>
          <AnimatedCollapsibleContent open={restrictionsOpen}>
            <PlateEditor
              ref={restrictionsRef}
              id="restrictions"
              value={data.restrictions}
              onChange={(value) => setData("restrictions", value)}
              onTab={() => submitButtonRef.current?.focus()}
              onArrowUp={() => requestRef.current?.focusAtEnd()}
              onArrowDown={() => submitButtonRef.current?.focus()}
              placeholder="Enter restrictions..."
            />
            {errors.restrictions && (
              <FieldError>
                {Array.isArray(errors.restrictions) ? errors.restrictions.join(", ") : errors.restrictions}
              </FieldError>
            )}
          </AnimatedCollapsibleContent>
        </Field>
      </Collapsible>
      */}

      {submitLabel && (
        <Field className="gap-4 px-8 pt-4 pb-8">
          <Button
            ref={submitButtonRef}
            type="submit"
            disabled={processing}
          >
            {processing ? "Saving..." : submitLabel}
          </Button>
        </Field>
      )}

    </form>
  )
}
